import { Popover } from '@mui/material';
import type { ConferenceFilter, TeamConferenceFilter } from '@on3/api';
import { useSite } from '@on3/ui-lib/contexts/SiteProvider';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { Arrow } from '@public/components/Svg/Arrow';
import { PrimaryButton } from '@public/components/UI/Buttons/PrimaryButton';
import { Text } from '@public/components/UI/Typography/Text';
import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  type MouseEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';

import styles, {
  focusBackground,
  gray,
  lightGray,
  night,
  on3Primary,
} from './Filter.module.scss';

interface IFilter {
  name: string;
  list?: ConferenceFilter[];
  selected?: string;
  teams?: TeamConferenceFilter[];
  buttonClassName?: string;
  collegePathName?: string;
  useOrgKey?: boolean;
  fillColor?: string;
  showClear?: boolean;
}

const anchorOrigin = {
  vertical: 'bottom',
  horizontal: 'center',
} as const;
const transformOrigin = {
  vertical: 'top',
  horizontal: 'center',
} as const;

const zIndexStyle = { zIndex: 10000 };

export const FilterTeams = ({
  name,
  teams,
  selected,
  buttonClassName,
  collegePathName,
  useOrgKey = false,
  fillColor,
  showClear,
}: IFilter) => {
  const router = useRouter();
  const { formatHref } = useDynamicRoute();

  const { currentSite, defaultYears } = useSite();
  const { footballRecruitingYear, nonFootballRecruitingYear } = defaultYears;
  const { query, pathname } = useRouter();
  const { conference, college, 'team-key': teamKey, sport } = query;

  const recruitingYear =
    sport === 'football' ? footballRecruitingYear : nonFootballRecruitingYear;
  const [anchorEl, setAnchorEl] = useState(null);
  const isLatestCommitsPage = router.asPath.includes('latest-commits');
  const isNILRankings = router.asPath.includes('/nil/rankings/');
  const isLatestDecommitsPage = router.asPath.includes('latest-decommits');
  const isNil = currentSite?.key === 368;
  const [selectedConference, setSelectedConference] = useState<any>(
    currentSite?.conference?.key || conference || 1,
  );
  const primaryColor =
    currentSite?.linkColor || currentSite?.primaryColor || on3Primary;

  //current team name, used for when not on a TeamSite
  const [selectedTeam, setSelectedTeam] = useState(
    currentSite?.organization?.knownAs ??
      currentSite?.organization?.name ??
      'team',
  );

  const [listTeams, setListTeams] = useState(
    teams?.filter((filterTeam) => {
      return filterTeam.conferenceKey === selectedConference;
    }),
  );

  const handleSelected = useCallback(
    (key?: string | number | null) => () => {
      setSelectedConference(key);
    },
    [],
  );
  const handleClick: MouseEventHandler<any> = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const getNextHref = useCallback(
    (value?: string | number | null) => {
      if (isLatestCommitsPage) {
        return {
          pathname: `/college/${value}/${
            sport ?? 'football'
          }/${recruitingYear}/industry-comparison-commits`,
        };
      }

      if (isLatestDecommitsPage) {
        return {
          pathname: `/college/${value}/${
            sport ?? 'football'
          }/${recruitingYear}/industry-comparison-decommits`,
        };
      }

      if (isNILRankings) {
        return {
          pathname: `/nil/rankings/player/[organizationType]/[sport]/`,
          query: {
            ...query,
            organizationType: 'college',
            sport: sport ?? 'football',
            'team-key': value,
          },
        };
      }

      return {
        pathname: collegePathName || pathname,
        query: {
          ...query,
          [name]: value,
        },
      };
    },
    [
      collegePathName,
      isLatestCommitsPage,
      isLatestDecommitsPage,
      isNILRankings,
      name,
      pathname,
      query,
      recruitingYear,
      sport,
    ],
  );
  //Set selected conference for when not on a Team Site
  const getSelectedConference = useCallback(() => {
    teamKey &&
      teams?.map((conf) => {
        conf?.teams?.map((team) => {
          if (+teamKey === team.orgKey) {
            conf.conferenceKey && setSelectedConference(conf?.conferenceKey);
          }
        });
      });
  }, [teamKey, teams]);

  const handleClearTeams = useCallback(() => {
    handleClose();
    router.push(formatHref({ query: { 'team-key': undefined } }));
  }, [formatHref, handleClose, router]);

  useEffect(() => {
    setListTeams(teams?.filter((c) => c.conferenceKey === selectedConference));
    if (conference) {
      setSelectedConference(conference);
    }
  }, [conference, selected, selectedConference, teamKey, teams]);

  //Set team name and selected conference on pageload or when teamKey is changed
  useEffect(() => {
    const key: number | undefined = teamKey
      ? +teamKey
      : selected
        ? +selected
        : undefined;

    if (key) {
      teams?.map((selectedConf) => {
        const findTeam = selectedConf.teams?.find(
          (select) => select.orgKey === +key,
        );

        findTeam?.orgName && setSelectedTeam(findTeam.orgName);
        getSelectedConference();
      });
    }

    if (isNil && !key) {
      setSelectedTeam('Teams');
    }
  }, [getSelectedConference, isNil, selected, teamKey, teams]);

  return (
    <div className={styles.filterWrapper}>
      <PrimaryButton
        aria-describedby={id}
        className={clsx(styles.filterButton, buttonClassName)}
        fullWidth={false}
        onClick={handleClick}
        variant="text"
      >
        {selectedTeam}
        <Arrow className={styles.arrow} fill={fillColor || primaryColor} />
      </PrimaryButton>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        id={id}
        onClose={handleClose}
        open={open}
        style={zIndexStyle}
        transformOrigin={transformOrigin}
      >
        <>
          <Text className={styles.conferenceWrapper}>
            {showClear && (
              <button
                data-type="clear"
                onClick={handleClearTeams}
                style={{ backgroundColor: primaryColor }}
              >
                Teams
              </button>
            )}
            {teams?.map((conferenceTeams) => {
              const isActive =
                conferenceTeams.conferenceKey === selectedConference;
              const conferenceStyle = isActive
                ? {
                    color: fillColor || primaryColor,
                    backgroundColor: focusBackground,
                    borderColor: fillColor || primaryColor,
                    border: '1px solid',
                  }
                : { color: gray, backgroundColor: lightGray };

              return (
                <PrimaryButton
                  className={styles.btnConference}
                  fullWidth={false}
                  key={conferenceTeams.conferenceKey}
                  onClick={handleSelected(conferenceTeams.conferenceKey)}
                  style={conferenceStyle}
                  variant="text"
                >
                  <Text
                    className={styles.conferenceText}
                    color="inherit"
                    variant="subtitle1"
                  >
                    {conferenceTeams.conferenceAbbr}
                  </Text>
                </PrimaryButton>
              );
            })}
          </Text>
          <Text className={clsx(styles.wrapper, styles.teamWrapper)}>
            {listTeams?.map((con) => {
              return con?.teams?.map((conferenceTeam) => {
                const isActive =
                  college === conferenceTeam.siteSlug?.toLowerCase() ||
                  teamKey === `${conferenceTeam.orgKey}`;
                const itemStyle = {
                  color: isActive ? night : fillColor || primaryColor,
                };

                return (
                  <Link
                    href={getNextHref(
                      useOrgKey
                        ? conferenceTeam.orgKey
                        : conferenceTeam.siteSlug,
                    )}
                    key={conferenceTeam.orgKey}
                    onClick={handleClose}
                    passHref
                  >
                    <Text
                      className={styles.link}
                      color="primary"
                      style={itemStyle}
                      variant="subtitle1"
                    >
                      {conferenceTeam.orgName}
                    </Text>
                  </Link>
                );
              });
            })}
          </Text>
        </>
      </Popover>
    </div>
  );
};
